import type { CreateListingActionRequest, ListingActionDto } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Plus, Store } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { pickPlace, placeOptions } from '~/entities/listing/lib/place-picker'
import { useListing, useListings } from '~/entities/listing/model/use-listings'
import {
  useCreateListingAction,
  useEndListingAction,
  useListingActions,
} from '~/entities/listing-action/model/use-listing-actions'
import { listingsInScope } from '~/entities/profile/lib/profile-scope'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'
import { actionsPageView } from '../lib/actions-view'
import { ActionComposerCard } from './action-composer-card'
import { ActionHistory } from './action-history'
import { ActionTodayCard } from './action-today-card'

export function ActionsPage({ listingId: wantedId }: { listingId?: string }) {
  const locale = getLocale() === 'en' ? 'en' : 'de'
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const places = useListings(businessId)
  const { scope } = useProfileScope()
  const [pickedId, setSelectedId] = useState(wantedId)
  const selectedId = scope.kind === 'place' ? scope.listingId : pickedId
  const onFirstPage = !selectedId || places.listings.some((place) => place.id === selectedId)
  const requested = useListing(businessId, onFirstPage ? '' : (selectedId ?? ''))
  const options = listingsInScope(placeOptions(places.listings, requested.data), scope)
  const listing = requested.isLoading ? null : pickPlace(options, selectedId)
  const listingId = listing?.id ?? ''
  const actions = useListingActions(businessId, listingId)
  const create = useCreateListingAction(businessId)
  const end = useEndListingAction(businessId)

  async function send(input: CreateListingActionRequest): Promise<boolean> {
    try {
      await create.mutateAsync({ listingId, input })
      toast.success(m.actionSent())
      return true
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
      return false
    }
  }

  async function endEarly(action: ListingActionDto) {
    try {
      await end.mutateAsync({ listingId, actionId: action.id })
      toast.success(m.actionEnded())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  const picker = options.length > 1 && (
    <Select value={listingId} onValueChange={setSelectedId}>
      <SelectTrigger className="w-full sm:w-64" aria-label={m.actionsPlacePicker()}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((place) => (
          <SelectItem key={place.id} value={place.id}>
            {place.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  function body() {
    if (!business || places.isLoading || requested.isLoading || (listing && actions.isLoading)) {
      return (
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      )
    }
    if (!listing) {
      return (
        <Card className="flex flex-col items-center gap-3 py-16 text-center">
          <Store className="size-8 text-muted-foreground" aria-hidden />
          <p className="max-w-sm text-muted-foreground text-sm">{m.actionsNoPlaces()}</p>
          <Button asChild>
            <Link to="/setup/profile" search={{ kind: 'place' }}>
              <Plus />
              {m.newPlace()}
            </Link>
          </Button>
        </Card>
      )
    }
    if (!actions.data) {
      return (
        <Card className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-muted-foreground text-sm">{m.actionsLoadError()}</p>
          <Button type="button" variant="outline" onClick={() => actions.refetch()}>
            {m.tryAgain()}
          </Button>
        </Card>
      )
    }
    const view = actionsPageView(actions.data)
    return (
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-6">
          {view.current && (
            <ActionTodayCard
              action={view.current}
              sentToday={view.sentToday}
              locale={locale}
              ending={end.isPending}
              onEnd={() => view.current && endEarly(view.current)}
            />
          )}
          {view.canSend && (
            <ActionComposerCard
              placeName={listing.name}
              favoriteCount={actions.data.favoriteCount}
              replaces={view.replaces}
              locale={locale}
              sending={create.isPending}
              onSend={send}
            />
          )}
        </div>
        <ActionHistory actions={actions.data.recent} locale={locale} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.actionsPageTitle()}
        description={m.actionsPageDescription()}
        action={picker}
      />
      {body()}
    </div>
  )
}
